// Copyright (c) Said Borna. All rights reserved.
import { Component, type ErrorInfo, type ReactNode, type ReactElement } from 'react';

const CHUNK_RELOAD_KEY = 'bam:chunk-reload-attempted';

function isChunkLoadError(error: Error): boolean {
    const message = error.message.toLowerCase();
    return (
        message.includes('failed to fetch dynamically imported module')
        || message.includes('importing a module script failed')
        || message.includes('loading chunk')
        || message.includes('chunkloaderror')
    );
}

/** Props for the ErrorBoundary component. */
interface ErrorBoundaryProps {
    /** Content to render when no error has occurred. */
    readonly children: ReactNode;
    /** Optional custom fallback UI. If not provided, uses default error UI. */
    readonly fallback?: ReactNode;
    /** Optional callback when an error is caught. */
    readonly onError?: (error: Error, errorInfo: ErrorInfo) => void;
    /** Context label for error reporting (e.g. route or feature name). */
    readonly context?: string;
}

/** State for the ErrorBoundary component. */
interface ErrorBoundaryState {
    readonly hasError: boolean;
    readonly error: Error | null;
}

/**
 * React error boundary that catches rendering errors in child components.
 * Prevents the entire app from crashing when a single route or component fails.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary context="Dashboard">
 *   <DashboardPage />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        const context = this.props.context ?? 'Unknown';
        console.error(`[ErrorBoundary:${context}] Caught error:`, error, errorInfo);

        // Recover from stale lazy-loaded chunks after deploy by forcing one hard reload.
        if (isChunkLoadError(error)) {
            const alreadyReloaded = window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
            if (!alreadyReloaded) {
                window.sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
                window.location.reload();
                return;
            }
        }

        this.props.onError?.(error, errorInfo);
    }

    private handleRetry = (): void => {
        this.setState({ hasError: false, error: null });
    };

    private handleGoHome = (): void => {
        window.location.href = '/';
    };

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <ErrorFallback
                    error={this.state.error}
                    context={this.props.context}
                    onRetry={this.handleRetry}
                    onGoHome={this.handleGoHome}
                />
            );
        }

        return this.props.children;
    }
}

/** Props for the default error fallback UI. */
interface ErrorFallbackProps {
    readonly error: Error | null;
    readonly context?: string;
    readonly onRetry: () => void;
    readonly onGoHome: () => void;
}

/**
 * Default error fallback UI.
 * Shows a user-friendly error message with retry and home navigation options.
 */
// eslint-disable-next-line react-refresh/only-export-components -- ErrorFallback is a private helper, not a separate export
function ErrorFallback({ error, context, onRetry, onGoHome }: ErrorFallbackProps): ReactElement {
    return (
        <div className="flex min-h-[400px] items-center justify-center bg-[#FAFAFA] p-8">
            <div className="w-full max-w-md rounded-xl border border-border bg-white p-8 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--destructive))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
                </div>
                <h2 className="mb-2 text-lg font-semibold text-foreground">
                    Something went wrong
                </h2>
                <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
                    {context
                        ? `An error occurred in the ${context} section.`
                        : 'An unexpected error occurred.'}
                    {' '}Please try again or return to the home page.
                </p>
                {error && import.meta.env.DEV && (
                    <details className="mb-6 rounded-lg border border-border bg-[#F9FAFB] p-3 text-left">
                        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                            Error details (dev only)
                        </summary>
                        <pre className="mt-2 overflow-auto font-mono text-xs text-destructive">
                            {error.message}
                        </pre>
                    </details>
                )}
                <div className="flex items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={onRetry}
                        className="rounded-lg bg-foreground px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-foreground/90 active:scale-[0.98]"
                    >
                        Try again
                    </button>
                    <button
                        type="button"
                        onClick={onGoHome}
                        className="rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-[#F9FAFB] active:scale-[0.98]"
                    >
                        Go home
                    </button>
                </div>
            </div>
        </div>
    );
}
